import { useState } from "react";
import { useShallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import { Button, CustomerDetails, NextBillCharges, BillingNotification, TermsNconditions, Typography } from "mobily-ui-library"
import { useGlobalStore } from "../stores/globalStore";
import { useCheckoutStore } from "../stores/checkoutStore";
import { usePlaceOrder } from "../hooks/usePlaceOrder";
import { getProductTitle } from "../utils";
import { ByPassOTPError } from "./ByPassOTPError";
import OrderSummaryImage from "../assets/OrderSummaryImage.png";
import "./OrderSummary.scss";

const findPrepaidPricing = (productOfferingPrice: any[]) => {
    let sixMonthPrice = 0;
    let twelveMonthPrice = 0;
    productOfferingPrice.forEach((priceObj) => {
        const name = priceObj?.name?.toLowerCase().replace(/\s+/g, "") || "";
        if (name.includes("6months")) {
            sixMonthPrice = priceObj?.price?.value || 0;
        } else if (name.includes("1year") || name.includes("12months")) {
            twelveMonthPrice = priceObj?.price?.value || 0;
        }
    });
    return { sixMonthPrice, twelveMonthPrice };
}

export const OrderSummary = () => {
    const { t } = useTranslation();

    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const { shoppingCart, setRequestRefNumber, graphqlResponse, lang, locationState, formState, isMeshExtenderOpted, otpReferenceId, bypassOTPError } = useGlobalStore(
        useShallow((state) => ({
            shoppingCart: state.shoppingCart,
            setRequestRefNumber: state.setRequestRefNumber,
            graphqlResponse: state.graphqlResponse,
            lang: state.lang,
            locationState: state.locationState,
            formState: state.formState,
            isMeshExtenderOpted: state.isMeshExtenderOpted,
            otpReferenceId: state.otpReferenceId,
            bypassOTPError: state.bypassOTPError,
        }))
    );
    const { goToNextSection } = useCheckoutStore(useShallow((state) => ({
        goToNextSection: state.goToNextSection,
    })));
    const placeOrder = usePlaceOrder();
    const handleConfirmOrder = async () => {
        if (!isTermsChecked) return;
        const res = await placeOrder({scId: shoppingCart?.id, otpReferenceId: otpReferenceId ?? ''});
        const requestRefNumber = res.data?.data?.orderId || "";
        setRequestRefNumber(requestRefNumber);
        goToNextSection();
    }

    const productTitle = getProductTitle(graphqlResponse, lang ?? 'en');
    const bgImageUrl = graphqlResponse?.data?.getProductOfferByID?.[0]?.attachment?.[0]?.url || OrderSummaryImage;

    const journeyName = sessionStorage.getItem("journey");
    const itemName = sessionStorage.getItem("gtm_item_list_name");
    
    const isPostpaid = itemName === "fiber-postpaid" ;
    const isPrepaid = itemName === "fiber-prepaid" ;

    let prepaidMonths;
    let freeMonths;
    let planCharges ;

    if (isPrepaid) {
        const productOfferingPrice = graphqlResponse?.data?.getProductOfferByID?.[0]?.productOfferingPrice || [];
        const { sixMonthPrice, twelveMonthPrice } = findPrepaidPricing(productOfferingPrice);
        
        if (journeyName === "FTTH_6:Month") {
            prepaidMonths = 6;
            freeMonths = 1;
            planCharges = sixMonthPrice;
        } else if (journeyName === "FTTH_12:Month") {
            prepaidMonths = 12;
            freeMonths = 2;
            planCharges = twelveMonthPrice;
        }
    }
    
    if(isPostpaid){
        planCharges = graphqlResponse?.data?.getProductOfferByID?.[0]?.productOfferingPrice?.[0]?.price?.value;
    }
    const vatAmount = Math.round(planCharges * 0.15 * 100) / 100;
    const termsUrl = (window as any)["terms_conditions_url"];
    
    const rtl =  lang === 'ar' ? true : false;

    return (
        <div className="orderSummary">
            <div className="orderSummary__wrapper">
                <div className="orderSummary__header">
                    <Typography className="orderSummary__heading">{t("orderSummaryTitle")}</Typography>
                </div>
                <div
                    className="orderSummary__image"
                    style={{
                        backgroundImage: `linear-gradient(270deg,
                           rgba(0, 0, 0, 7.4e-05) 0%, 
                          rgba(0, 0, 0, 0.74) 100% ),url(${bgImageUrl})`,
                    }}
                >
                    <h3 className={lang === 'ar' ? 'arabicHeading' : ''}>{productTitle}</h3>
                    <p>{isPostpaid ? t("monthly_subs") : t("plan_subs", { months: prepaidMonths, freeMonths })}</p>
                </div>
                <CustomerDetails
                    title={t("orderSummaryYourDetails")}
                    addressTitle={t("orderSummaryAddressDetails")}
                    name={`${formState.firstName} ${formState.lastName}`}
                    phone={`05${formState.contactNumber}`}
                    email={formState.emailAddress}
                    address={locationState?.address}
                />
                <NextBillCharges
                    charges={[
                        {
                            label: t("orderSummaryPlanCharges", { planName: productTitle }),
                            amount: planCharges,
                        },
                        ...(isMeshExtenderOpted ? [{
                            label: t("orderSummaryWifiExtenderCharges", { count: 2 }),
                            amount: 0,
                        }] : []),
                        {
                            label: t("orderSummary_VAT", { charge: 15 }),
                            amount: vatAmount,
                        },
                    ]}
                    total={planCharges + vatAmount}
                    vatText={t("orderSummaryTotal_Incl_VAT", { charge: 15 })}
                    title={isPostpaid ? t("orderSummaryNextBillCharges") : t("orderSummaryOrderCharges")}
                    totalText={t("orderSummaryTotal")}
                />
                <BillingNotification
                    message={isPostpaid ?
                        t("orderSummaryNote_1")
                        : t("orderSummaryNote_2")
                    }
                />
                <TermsNconditions
                    termsTitle={t("Mobily_Shops")}
                    termsUrlTitle={t("terms_conditions")}
                    termsUrl={termsUrl}
                    isCheckCallBack={(val: boolean) => setIsTermsChecked(val)}
                    rtl={rtl}
                />
                <div className="orderSummary__btn-wrapper">
                    <Button
                        id="confirmOrderBtn"
                        variant={isTermsChecked ? "default" : "disabled"}
                        onClick={handleConfirmOrder}>
                        {t("confirmOrderBtn")}
                    </Button>
                </div>
            </div>
            {bypassOTPError && <ByPassOTPError />}
        </div>
    )
}